import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Button, Dropdown, Modal, showSnackbar, TextInput } from 'fluxo-ui';
import {
    AngleLeftIcon,
    AngleRightIcon,
    CopyIcon,
    DownloadIcon,
    EditIcon,
    PlusIcon,
    QuestionCircle,
    SearchIcon,
    TrashIcon,
    UploadIcon,
} from 'fluxo-ui/icons';

import { QUESTION_TYPES } from '../common/constants';
import { Badge, Card, EmptyState, PageHeader } from '../components/common';
import { CSVImporter } from '../components/question';
import questionService from '../services/question.service';
import { useCategoryStore } from '../store/categoryStore';
import { useQuestionStore } from '../store/questionStore';

import type { BadgeTone } from '../components/common/Badge';

const ROWS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 350;

const difficultyTones: Record<string, BadgeTone> = {
    beginner: 'success',
    easy: 'success',
    intermediate: 'warning',
    hard: 'danger',
    expert: 'danger',
};

const statusTones: Record<string, BadgeTone> = {
    draft: 'neutral',
    pending_review: 'warning',
    approved: 'success',
    rejected: 'danger',
};

const QuestionBankPage: React.FC = () => {
    const navigate = useNavigate();
    const { questions, totalCount, loading, fetchQuestions, deleteQuestion, cloneQuestion } = useQuestionStore();
    const { categories, fetchCategories } = useCategoryStore();

    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
    const [showImportModal, setShowImportModal] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        const filters: any = {
            page,
            limit: ROWS_PER_PAGE,
        };

        if (searchQuery) filters.query = searchQuery;
        if (selectedType) filters.type = selectedType;
        if (selectedCategory) filters.category = selectedCategory;

        fetchQuestions(filters);
    }, [page, searchQuery, selectedType, selectedCategory, fetchQuestions]);

    const handleDelete = async () => {
        if (!questionToDelete) return;

        try {
            await deleteQuestion(questionToDelete);
            showSnackbar('Question deleted successfully', 'Success', { type: 'success' });
            setDeleteModalOpen(false);
            setQuestionToDelete(null);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to delete question', 'Error', { type: 'error' });
        }
    };

    const handleClone = async (id: string) => {
        try {
            await cloneQuestion(id);
            showSnackbar('Question cloned successfully', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to clone question', 'Error', { type: 'error' });
        }
    };

    const handleImportComplete = () => {
        setShowImportModal(false);
        fetchQuestions({ page, limit: ROWS_PER_PAGE });
    };

    const handleExport = async () => {
        try {
            await questionService.exportQuestionsCSV();
            showSnackbar('Question bank exported', 'Download started', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to export questions', 'Error', { type: 'error' });
        }
    };

    const questionTypeOptions = Object.entries(QUESTION_TYPES).map(([key, value]) => ({
        label: key.replace(/_/g, ' ').toUpperCase(),
        value,
    }));

    const categoryOptions = categories.map((cat) => ({
        label: cat.name,
        value: cat._id,
    }));

    const totalPages = Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE));

    return (
        <div className="space-y-6">
            <PageHeader
                title="Question Bank"
                description="Manage your questions and create new ones"
                className="mb-0"
                actions={
                    <>
                        <Button label="Export CSV" variant="default" layout="outlined" leftIcon={DownloadIcon} onClick={handleExport} />
                        <Button
                            label="Import CSV"
                            variant="default"
                            layout="outlined"
                            leftIcon={UploadIcon}
                            onClick={() => setShowImportModal(true)}
                        />
                        <Button
                            label="Create Question"
                            variant="primary"
                            leftIcon={PlusIcon}
                            onClick={() => navigate('/questions/create')}
                        />
                    </>
                }
            />

            <Card padding="sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <TextInput
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.value)}
                        placeholder="Search questions..."
                        leftIcon={<SearchIcon />}
                    />
                    <Dropdown
                        options={[{ label: 'All Types', value: null }, ...questionTypeOptions]}
                        value={selectedType}
                        onChange={(e) => {
                            setSelectedType(e.value);
                            setPage(1);
                        }}
                        placeholder="Filter by type"
                        showClear
                    />
                    <Dropdown
                        options={[{ label: 'All Categories', value: null }, ...categoryOptions]}
                        value={selectedCategory}
                        onChange={(e) => {
                            setSelectedCategory(e.value);
                            setPage(1);
                        }}
                        placeholder="Filter by category"
                        showClear
                    />
                </div>
            </Card>

            {loading ? (
                <Card>
                    <div className="text-center py-10 text-slate-500 dark:text-slate-400">Loading questions...</div>
                </Card>
            ) : questions.length === 0 ? (
                <Card>
                    <EmptyState
                        icon={QuestionCircle}
                        title="No questions found"
                        description="Create your first question to get started, or adjust your filters."
                        action={
                            <Button
                                label="Create Question"
                                variant="primary"
                                leftIcon={PlusIcon}
                                onClick={() => navigate('/questions/create')}
                            />
                        }
                    />
                </Card>
            ) : (
                <div className="space-y-3">
                    {questions.map((question: any) => (
                        <div
                            key={question._id}
                            className="group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm transition-all p-4 cursor-pointer"
                            onClick={() => navigate(`/questions/${question._id}/edit`)}
                        >
                            <div className="flex justify-between items-start gap-3">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                                        {question.title || 'Untitled'}
                                    </h3>
                                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        ID: {question.questionId || question._id.substring(0, 8)}
                                    </div>
                                    <div className="flex gap-2 mt-3 flex-wrap">
                                        <Badge tone="accent">{question.type?.replace(/_/g, ' ')}</Badge>
                                        {question.difficulty && (
                                            <Badge tone={difficultyTones[question.difficulty] || 'neutral'}>{question.difficulty}</Badge>
                                        )}
                                        {question.status && (
                                            <Badge tone={statusTones[question.status] || 'neutral'} dot>
                                                {question.status.replace(/_/g, ' ')}
                                            </Badge>
                                        )}
                                        {Array.isArray(question.category) &&
                                            question.category.map((cat: any, i: number) => (
                                                <Badge key={i} tone="neutral">
                                                    {cat.name || cat}
                                                </Badge>
                                            ))}
                                    </div>
                                </div>
                                <div className="flex gap-1 shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                    <Button
                                        leftIcon={EditIcon}
                                        layout="plain"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/questions/${question._id}/edit`);
                                        }}
                                        ariaLabel="Edit question"
                                    />
                                    <Button
                                        leftIcon={CopyIcon}
                                        layout="plain"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleClone(question._id);
                                        }}
                                        ariaLabel="Clone question"
                                    />
                                    <Button
                                        leftIcon={TrashIcon}
                                        layout="plain"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setQuestionToDelete(question._id);
                                            setDeleteModalOpen(true);
                                        }}
                                        ariaLabel="Delete question"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 px-4 py-3">
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                            Showing{' '}
                            <span className="font-medium text-slate-900 dark:text-white tabular-nums">
                                {(page - 1) * ROWS_PER_PAGE + 1}–{(page - 1) * ROWS_PER_PAGE + questions.length}
                            </span>{' '}
                            of <span className="font-medium text-slate-900 dark:text-white tabular-nums">{totalCount}</span> questions
                        </span>
                        <div className="flex items-center gap-2">
                            <Button
                                leftIcon={AngleLeftIcon}
                                layout="outlined"
                                variant="default"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() => setPage(page - 1)}
                                ariaLabel="Previous page"
                            />
                            <span className="text-sm text-slate-600 dark:text-slate-300 tabular-nums">
                                {page} / {totalPages}
                            </span>
                            <Button
                                leftIcon={AngleRightIcon}
                                layout="outlined"
                                variant="default"
                                size="sm"
                                disabled={page >= totalPages}
                                onClick={() => setPage(page + 1)}
                                ariaLabel="Next page"
                            />
                        </div>
                    </div>
                </div>
            )}

            <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Delete Question" size="sm">
                <div className="space-y-4">
                    <p className="text-slate-600 dark:text-slate-400">
                        Are you sure you want to delete this question? This action cannot be undone.
                    </p>
                    <div className="flex gap-3 justify-end">
                        <Button label="Cancel" variant="default" layout="outlined" onClick={() => setDeleteModalOpen(false)} />
                        <Button label="Delete" variant="danger" onClick={handleDelete} />
                    </div>
                </div>
            </Modal>

            <Modal isOpen={showImportModal} onClose={() => setShowImportModal(false)} title="Import Questions from CSV" size="lg">
                <CSVImporter onImportComplete={handleImportComplete} />
            </Modal>
        </div>
    );
};

export default QuestionBankPage;
